import * as React from 'react'

import { cn } from './cn'

export type TypographyVariant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'subtitle1'
  | 'subtitle2'
  | 'body1'
  | 'body2'
  | 'caption'
  | 'overline'

export interface TypographyProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'color'> {
  variant?: TypographyVariant
  component?: React.ElementType
  gutterBottom?: boolean
  noWrap?: boolean
  align?: 'left' | 'center' | 'right' | 'justify' | 'inherit'
  color?: string
  paragraph?: boolean
  /** Forwarded when rendering as a label. */
  htmlFor?: string
  fontSize?: number | string
  fontWeight?: number | string
  lineHeight?: number | string
  fontStyle?: React.CSSProperties['fontStyle']
  textAlign?: React.CSSProperties['textAlign']
  flexGrow?: React.CSSProperties['flexGrow']
  paddingTop?: number | string
  /** MUI spacing units. */
  mb?: number
  mt?: number
}

const variantTags: Record<TypographyVariant, React.ElementType> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  subtitle1: 'h6',
  subtitle2: 'h6',
  body1: 'p',
  body2: 'p',
  caption: 'span',
  overline: 'span',
}

const variantClasses: Record<TypographyVariant, string> = {
  h1: 'text-[4.5rem] font-bold leading-[1.05] tracking-tight',
  h2: 'text-[3.25rem] font-bold leading-[1.1] tracking-tight',
  h3: 'text-[2.5rem] font-semibold leading-[1.15] tracking-tight',
  h4: 'text-[1.875rem] font-semibold leading-snug tracking-tight',
  h5: 'text-2xl font-semibold leading-snug tracking-tight',
  h6: 'text-lg font-semibold leading-relaxed',
  subtitle1: 'text-base font-medium leading-7',
  subtitle2: 'text-sm font-medium leading-6',
  body1: 'text-base font-normal leading-relaxed',
  body2: 'text-sm font-normal leading-normal',
  caption: 'block text-xs font-normal leading-relaxed text-text-secondary',
  overline:
    'block text-xs font-medium uppercase tracking-widest leading-loose text-text-secondary',
}

const alignClasses = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
  justify: 'text-justify',
  inherit: '',
}

/**
 * Maps the common MUI `color` prop values used in this codebase.
 * Anything unrecognized is passed through as an inline style.
 */
const colorClassMap: Record<string, string> = {
  primary: 'text-primary',
  'primary.main': 'text-primary',
  secondary: 'text-primary',
  textPrimary: 'text-text-primary',
  'text.primary': 'text-text-primary',
  textSecondary: 'text-text-secondary',
  'text.secondary': 'text-text-secondary',
  'text.disabled': 'text-text-disabled',
  error: 'text-error',
  'error.main': 'text-error',
  warning: 'text-warning',
  'warning.main': 'text-warning',
  success: 'text-success',
  'success.main': 'text-success',
  info: 'text-info',
  'info.main': 'text-info',
  inherit: 'text-inherit',
  white: 'text-white',
}

const Typography = React.forwardRef<HTMLElement, TypographyProps>(
  (
    {
      variant = 'body1',
      component,
      gutterBottom,
      noWrap,
      align,
      color,
      paragraph,
      fontSize,
      fontWeight,
      lineHeight,
      fontStyle,
      textAlign,
      flexGrow,
      paddingTop,
      mb,
      mt,
      className,
      style,
      children,
      ...props
    },
    ref,
  ) => {
    const Component = component || (paragraph ? 'p' : variantTags[variant])
    const colorClass = color ? colorClassMap[color] : undefined
    const colorStyle = {
      ...(color && !colorClass ? { color } : undefined),
      ...(fontSize !== undefined ? { fontSize } : undefined),
      ...(fontWeight !== undefined ? { fontWeight } : undefined),
      ...(lineHeight !== undefined ? { lineHeight } : undefined),
      ...(fontStyle !== undefined ? { fontStyle } : undefined),
      ...(textAlign !== undefined ? { textAlign } : undefined),
      ...(flexGrow !== undefined ? { flexGrow } : undefined),
      ...(paddingTop !== undefined ? { paddingTop } : undefined),
      ...(mb !== undefined ? { marginBottom: mb * 8 } : undefined),
      ...(mt !== undefined ? { marginTop: mt * 8 } : undefined),
      ...style,
    }

    return (
      <Component
        ref={ref}
        className={cn(
          variantClasses[variant],
          align && alignClasses[align],
          gutterBottom && 'mb-[0.35em]',
          paragraph && 'mb-4',
          noWrap && 'overflow-hidden text-ellipsis whitespace-nowrap',
          colorClass,
          className,
        )}
        style={colorStyle}
        {...props}
      >
        {children}
      </Component>
    )
  },
)
Typography.displayName = 'Typography'

export default Typography
